import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { NotificationBell } from '../components/Notifications';
import {
  Activity,
  Heart,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  User,
  Dumbbell,
  Scale,
  Zap,
  Wind,
  ClipboardList,
  Apple,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const AthleteLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { 
      label: language === 'IT' ? 'Wellness' : 'Wellness',
      icon: Heart, 
      href: '/athlete/wellness',
    },
    { 
      label: language === 'IT' ? 'Peso' : 'Weight',
      icon: Scale, 
      href: '/athlete/weight',
    },
    { 
      label: 'VBT',
      icon: Dumbbell, 
      href: '/athlete/vbt',
    },
    { 
      label: language === 'IT' ? 'Programma' : 'Schedule',
      icon: Calendar, 
      href: '/athlete/schedule',
    },
    { 
      label: language === 'IT' ? 'Carico' : 'Load',
      icon: BarChart3, 
      href: '/athlete/load',
    },
    { 
      label: language === 'IT' ? 'Test Salto' : 'Jump Testing',
      icon: Zap, 
      href: '/athlete/jump',
    },
    { 
      label: language === 'IT' ? 'Test Aerobici' : 'Aerobic Testing',
      icon: Wind, 
      href: '/athlete/aerobic',
    },
    { 
      label: language === 'IT' ? 'Nutrizione' : 'Nutrition',
      icon: Apple, 
      href: '/athlete/nutrition',
    },
    { 
      label: language === 'IT' ? 'Prescrizioni' : 'Prescriptions',
      icon: ClipboardList, 
      href: '/athlete/prescriptions',
    },
  ];

  const isActive = (href) => location.pathname.startsWith(href);

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.href);

    if (item.disabled) {
      return (
        <div className="px-3 py-2 flex items-center gap-3 text-zinc-600 cursor-not-allowed">
          <Icon className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm font-medium">{item.label}</span>
          <span className="ml-auto text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-500">Soon</span>
        </div>
      );
    }

    return (
      <Link
        to={item.href}
        className={`px-3 py-2 flex items-center gap-3 rounded-sm transition-colors ${
          active
            ? 'bg-zinc-800/50 text-amber-500 border-l-2 border-amber-500'
            : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Mobile header — safe-area aware (notch / Dynamic Island) */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 -ml-2 text-zinc-400 hover:text-zinc-100"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2.5">
            <img src="/logo/fb-symbol-mobile.png" alt="FB Performance" className="w-7 h-7 object-contain" />
            <div className="leading-none">
              <span className="font-semibold text-sm text-zinc-100 block" data-testid="mobile-org-name">{user?.organization_name || 'FB Performance'}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell testId="notification-bell-mobile" />
            <button
              onClick={toggleLanguage}
              className="px-2 py-1 text-xs font-semibold tracking-wider uppercase text-zinc-400 hover:text-zinc-100"
            >
              {language}
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar — safe-area aware on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-zinc-950 border-r border-zinc-800 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
        }}
      >
        {/* Brand header */}
        <div className="py-5 px-5">
          <img src="/logo/fb-logo-dark.png" alt="FB Performance" className="h-8 lg:h-9 w-auto object-contain" data-testid="sidebar-logo" />
          {user?.organization_name && (
            <div className="mt-2 text-xs font-medium text-zinc-400 truncate" data-testid="sidebar-org-name">{user.organization_name}</div>
          )}
          <div className="mt-3 h-px bg-gradient-to-r from-zinc-700/60 via-zinc-800/40 to-transparent" />
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.href} item={item} />
          ))}
        </nav>

        {/* User menu */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800 bg-zinc-950">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-zinc-800/50 transition-colors" data-testid="user-menu">
                <div className="w-8 h-8 bg-amber-500/20 rounded-sm flex items-center justify-center text-amber-500 text-sm font-semibold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-zinc-100">{user?.first_name} {user?.last_name}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Atleta' : 'Athlete'}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-800" align="end">
              <DropdownMenuItem className="text-zinc-400 focus:text-zinc-100 focus:bg-zinc-800">
                <User className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Profilo' : 'Profile'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigate('/settings'); setSidebarOpen(false); }} className="text-zinc-400 focus:text-zinc-100 focus:bg-zinc-800" data-testid="settings-link">
                <Settings className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Impostazioni' : 'Settings'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleLanguage} className="text-zinc-400 focus:text-zinc-100 focus:bg-zinc-800">
                <span className="w-4 h-4 mr-2 text-center font-mono text-xs">{language}</span>
                Language / Lingua
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={handleLogout} className="text-rose-400 focus:text-rose-300 focus:bg-rose-500/10" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content — mobile pt = safe-area + header height (3.5rem); desktop overrides via lg:pt-0 */}
      <main className="lg:pl-64 pt-[calc(env(safe-area-inset-top)+3.5rem)] lg:pt-0">
        <div className="hidden lg:flex h-14 items-center justify-end gap-4 px-6 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-20">
          <NotificationBell testId="notification-bell-desktop" />
          <button
            onClick={toggleLanguage}
            className="px-3 py-1.5 text-xs font-semibold tracking-wider uppercase bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
            data-testid="desktop-language-toggle"
          >
            {language === 'EN' ? 'IT' : 'EN'}
          </button>
        </div>
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AthleteLayout;
